// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@stoneage/App/Components/Common/XngZone";
import { layoutContainer, xClassNames, xScreen } from "@stoneage/XDL/Utilities";
import * as cssLinter from "css";
import * as React from "react";
import { css } from "styled-components";
import { getProp } from "@insite/mobius/utilities";
import { constants, DivWrapper } from "@stoneage/XDL/constants";

// XNG-Changes: New options for Rows widget
const enum advanceRowsItemFields {
    name = "name",
    containerFullWidth = "containerFullWidth",
    containerMobileFullWidth = "containerMobileFullWidth",
    contentVerticalPadding = "contentVerticalPadding",
    backgroundColor = "backgroundColor",
}

const enum fields {
    rowCount = "rowCount",
    gap = "gap",
    isFullWidth = "isFullWidth",
    customCSS = "customCSS",
    // XNG-Changes
    isAdvanceRows = "isAdvanceRows",
    addFooterOffset = "addFooterOffset",
    advanceRowsItemsList = "advanceRowsItemsList",
}

interface AdvanceRowsItemProps {
    fields: {
        [advanceRowsItemFields.name]: string;
        [advanceRowsItemFields.containerFullWidth]: boolean;
        [advanceRowsItemFields.containerMobileFullWidth]: boolean;
        [advanceRowsItemFields.contentVerticalPadding]: string;
        [advanceRowsItemFields.backgroundColor]: string;
    };
}

interface Props extends WidgetProps {
    fields: {
        [fields.rowCount]: number;
        [fields.gap]: number;
        [fields.isFullWidth]: boolean;
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.isAdvanceRows]: boolean;
        [fields.advanceRowsItemsList]: AdvanceRowsItemProps[];
        [fields.addFooterOffset]: boolean;
    };
}

interface AdvanceRowsStyles {
    container?: InjectableCss;
    item?: InjectableCss;
}

export const advanceRowsStyles: AdvanceRowsStyles = {
    container: {
        css: css`
            max-width: 100%;
            width: 100%;
            display: flex;
            flex-direction: column;
            ${({ gap }: any) =>
                gap &&
                css`
                    gap: ${gap}px;
                `};

            ${({ addFooterOffset }: any) =>
                addFooterOffset &&
                css`
                    margin-bottom: ${constants.footer.offsetTop}px;
                `}

            @media print {
                display: block;
            }
        `,
    },
    item: {
        css: css`
            width: 100%;
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;

            ${({ backgroundColor }: any) =>
                backgroundColor &&
                css`
                    background-color: ${backgroundColor};
                `}

            ${({ contentVerticalPadding }: any) =>
                contentVerticalPadding &&
                css`
                    padding-top: ${contentVerticalPadding}px;
                    padding-bottom: ${contentVerticalPadding}px;
                `}
        ${({ containerMobileFullWidth }: any) =>
                containerMobileFullWidth &&
                css`
                    &&&.mobile-full-width {
                        ${xScreen("lte", "xs")} {
                            max-width: 100%;
                            margin-right: 0;
                            margin-left: 0;
                            padding-left: 0;
                            padding-right: 0;
                        }
                    }
                `}

            &:has(>${DivWrapper}:empty) {
                display: none;
            }

            // Check containerFullWidth by className
            ${layoutContainer}

            @media print {
                display: block;
                ${({ gap }: any) =>
                    gap &&
                    css`
                        margin: ${gap / 2}px 0;
                    `}
            }
        `,
    },
};

// XNG-Changes
const styles = advanceRowsStyles;
const AdvanceRows = ({ fields: { gap, advanceRowsItemsList, addFooterOffset }, id }: Props) => {
    const rows: JSX.Element[] = [];

    for (let row = 0; row < advanceRowsItemsList.length; row = row + 1) {
        const {
            name: rowName,
            containerFullWidth,
            containerMobileFullWidth,
            contentVerticalPadding,
            backgroundColor,
        } = advanceRowsItemsList[row].fields;

        rows.push(
            <StyledWrapper
                key={`rows-item-${id}-${rowName || row}`}
                data-test-selector={`rows-item-${rowName || row}`}
                className={xClassNames.append([
                    containerFullWidth && "full-width",
                    containerMobileFullWidth && "mobile-full-width",
                ])}
                {...{
                    backgroundColor,
                    contentVerticalPadding,
                    gap,
                    containerMobileFullWidth: !containerFullWidth && containerMobileFullWidth,
                }}
                {...styles.item}
            >
                <XngZone contentId={id} zoneName={`Content${rowName || row}`} hint={`Content for ${rowName || row}`} />
            </StyledWrapper>,
        );
    }

    return (
        <StyledWrapper {...{ gap, addFooterOffset }} {...styles.container}>
            {rows}
        </StyledWrapper>
    );
};

// A widget that makes a configurable number of row zones within its container.
const Rows = (props: Props) => {
    const {
        fields: { rowCount, gap, isFullWidth, customCSS, isAdvanceRows },
        id,
    } = props;

    if (isAdvanceRows) {
        return <AdvanceRows {...props} />;
    }

    const rows: JSX.Element[] = [];
    for (let row = 0; row < rowCount; row = row + 1) {
        rows.push(
            <GridItem
                key={`GridItem-${id}-${row}`}
                css={css`
                    &&&& {
                        @media print {
                            display: block;
                        }
                    }
                `}
                width={[12, 12, 12, 12, 12]}
            >
                <Zone contentId={id} zoneName={`Content${row}`} />
            </GridItem>,
        );
    }

    const gridCss = css`
        ${customCSS}
    `;

    return (
        <GridContainer css={gridCss} gap={gap} isFullWidth={isFullWidth}>
            {rows}
        </GridContainer>
    );
};

const defaultCustomCss = `& {

}`;

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Rows,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.isAdvanceRows,
                displayName: "Enable Advance Rows",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                variant: "toggle",
                fieldType: "General",
            },
            {
                name: fields.addFooterOffset,
                displayName: "Add footer offset",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                variant: "toggle",
                fieldType: "General",
                isVisible: item => item.fields.isAdvanceRows,
            },
            {
                name: fields.rowCount,
                displayName: "Row Count",
                editorTemplate: "IntegerField",
                defaultValue: 4,
                fieldType: "General",
                isVisible: item => !item.fields.isAdvanceRows,
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 16,
                fieldType: "General",
                max: 1000,
                min: 0,
            },
            // {
            //     name: fields.isFullWidth,
            //     displayName: "Full Width",
            //     editorTemplate: "CheckboxField",
            //     defaultValue: false,
            //     fieldType: "General",
            //     isVisible: (_item, _advancedFeaturesEnabled, theme) =>
            //         !!theme?.content?.isFullWidth && !_item.fields.isAdvanceRows,
            //     sortOrder: 4,
            // },
            {
                name: "customCSS",
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) =>
                    !!shouldDisplayAdvancedFeatures && !item.fields.isAdvanceRows,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            {
                name: fields.advanceRowsItemsList,
                displayName: "Advance row items",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.name || "Content Name";
                },
                defaultValue: [],
                fieldType: "General",
                isVisible: item => item.fields.isAdvanceRows,
                fieldDefinitions: [
                    {
                        name: advanceRowsItemFields.name,
                        displayName: "Content name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        placeholder: "Enter content name",
                        isRequired: true,
                    },
                    {
                        name: advanceRowsItemFields.containerFullWidth,
                        displayName: "Container Full Width",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                    },
                    // XNG-Changes: let row take full width on mobile if its container doesn't take full width
                    {
                        name: advanceRowsItemFields.containerMobileFullWidth,
                        displayName: "Container Mobile Full Width",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: item => !item.fields.containerFullWidth,
                    },
                    {
                        name: advanceRowsItemFields.contentVerticalPadding,
                        editorTemplate: "IntegerField",
                        defaultValue: 0,
                    },
                    {
                        name: advanceRowsItemFields.backgroundColor,
                        editorTemplate: "ColorPickerField",
                        displayName: "Background Color",
                        defaultValue: "",
                        popoverLocation: "top",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
